import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { compressImage } from '../utils/imageCompression'
import { Building2, Camera, Save, Globe, Users, FileText, User, Mail, MapPin, CalendarDays, Pencil } from 'lucide-react'
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { useAuthStore } from '../store/authStore'
import { supabaseClient } from '../config/supabase'
import toast from 'react-hot-toast'

interface CompanyData {
  id: string
  name: string
  industry: string
  company_size_text?: string
  website?: string
  description?: string
  location?: string
  logo_url?: string
}

interface ProfileFields {
  first_name: string
  last_name: string
  phone: string
  position: string
}

interface EmployerProfileRow {
  contact_person: string
  phone: string
  position: string
  approval_status?: string
  created_at?: string
}

interface PartnershipItem {
  department_id: string
  name: string
  abbreviation: string
  status: string
  rejection_reason?: string
  approved_at?: string
  updated_at?: string
}

interface DepartmentOption {
  id: string
  name: string
  abbreviation: string
}

const PARTNERSHIP_REAPPLY_COOLDOWN_DAYS = 3

const EmployerProfilePage: React.FC = () => {
  const { user, refreshUser } = useAuthStore()
  const [company, setCompany] = useState<CompanyData | null>(null)
  const [profileRecord, setProfileRecord] = useState<EmployerProfileRow | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [profileFields, setProfileFields] = useState<ProfileFields>({
    first_name: '',
    last_name: '',
    phone: '',
    position: '',
  })
  const [companyFields, setCompanyFields] = useState({
    description: '',
    location: '',
    website: '',
  })
  const [profilePicture, setProfilePicture] = useState<string>('')
  const [pictureFile, setPictureFile] = useState<File | null>(null)
  const [picturePreview, setPicturePreview] = useState<string>('')
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string>('')
  const [partnerships, setPartnerships] = useState<PartnershipItem[]>([])
  const [showPartnershipRequestModal, setShowPartnershipRequestModal] = useState(false)
  const [availableDepartments, setAvailableDepartments] = useState<DepartmentOption[]>([])
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('')
  const [loadingDepartments, setLoadingDepartments] = useState(false)
  const [requestingPartnership, setRequestingPartnership] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [isPartner, setIsPartner] = useState(false)

  useEffect(() => {
    fetchProfile()
  }, [user])

  const fetchProfile = async () => {
    if (!user) return
    try {
      const { data: ep } = await supabaseClient
        .from('employer_profiles')
        .select('*, companies(*)')
        .eq('user_id', user.id)
        .single()

      if (ep) {
        const co = (ep as any).companies as CompanyData
        setCompany(co)
        setProfileRecord({
          contact_person: ep.contact_person || '',
          phone: ep.phone || '',
          position: ep.position || '',
          approval_status: ep.approval_status,
          created_at: ep.created_at,
        })
        setIsPartner(ep.is_industry_partner || false)
        setProfilePicture(ep.profile_picture || '')
        setPicturePreview(ep.profile_picture || '')
        const nameParts = (ep.contact_person || '').split(' ')
        setProfileFields({
          first_name: nameParts[0] || '',
          last_name: nameParts.slice(1).join(' '),
          phone: ep.phone || '',
          position: ep.position || '',
        })
        setCompanyFields({
          description: co?.description || '',
          location: co?.location || '',
          website: co?.website || '',
        })
        setLogoPreview(co?.logo_url || '')

        if (ep.company_id) {
          const { data: pData } = await supabaseClient
            .from('company_department_partnerships')
            .select('department_id, status, rejection_reason, approved_at, updated_at, departments(name, abbreviation)')
            .eq('company_id', ep.company_id)
          setPartnerships(
            (pData || []).map((p: any) => ({
              department_id: p.department_id,
              name: p.departments?.name || '',
              abbreviation: p.departments?.abbreviation || '',
              status: p.status,
              rejection_reason: p.rejection_reason || undefined,
              approved_at: p.approved_at || undefined,
              updated_at: p.updated_at || undefined,
            }))
          )
        }
      }
    } catch (err) {
      console.error('Error fetching employer profile:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!user || !company || !profileRecord) return
    setSaving(true)
    try {
      // Upload profile picture if changed
      let newPictureUrl: string | null = profilePicture || null
      if (pictureFile) {
        const compressedPic = await compressImage(pictureFile, { maxWidth: 400, maxHeight: 400, quality: 0.85 })
        const fileName = `${user.id}-${Date.now()}.jpg`
        const filePath = `employer-profiles/${fileName}`
        const { error: uploadErr } = await supabaseClient.storage
          .from('employer-profiles')
          .upload(filePath, compressedPic, { upsert: true, contentType: 'image/jpeg' })
        if (uploadErr) {
          toast.error('Failed to upload profile picture')
          setSaving(false)
          return
        }
        const { data: urlData } = supabaseClient.storage.from('employer-profiles').getPublicUrl(filePath)
        newPictureUrl = urlData.publicUrl
      }

      // Update employer profile contact info + picture
      const { error: epErr } = await supabaseClient
        .from('employer_profiles')
        .update({
          contact_person: [profileFields.first_name.trim(), profileFields.last_name.trim()].filter(Boolean).join(' '),
          phone: profileFields.phone.trim(),
          position: profileFields.position.trim(),
          profile_picture: newPictureUrl,
        })
        .eq('user_id', user.id)

      if (epErr) throw epErr

      // Upload company logo if changed
      let newLogoUrl: string | null = company.logo_url || null
      if (logoFile) {
        const compressedLogo = await compressImage(logoFile, { maxWidth: 400, maxHeight: 400, quality: 0.85 })
        const logoName = `logo-${company.id}-${Date.now()}.jpg`
        const logoPath = `company-logos/${logoName}`
        const { error: logoUploadErr } = await supabaseClient.storage
          .from('employer-profiles')
          .upload(logoPath, compressedLogo, { upsert: true, contentType: 'image/jpeg' })
        if (logoUploadErr) {
          toast.error('Failed to upload company logo')
          setSaving(false)
          return
        }
        const { data: logoUrlData } = supabaseClient.storage.from('employer-profiles').getPublicUrl(logoPath)
        newLogoUrl = logoUrlData.publicUrl
      }

      // Update company fields
      const { error: coErr } = await supabaseClient
        .from('companies')
        .update({
          description: companyFields.description.trim(),
          location: companyFields.location.trim(),
          website: companyFields.website.trim() || null,
          logo_url: newLogoUrl,
        })
        .eq('id', company.id)

      if (coErr) throw coErr

      toast.success('Profile updated successfully!')
      setPictureFile(null)
      setLogoFile(null)
      setIsEditing(false)
      if (newPictureUrl) {
        setProfilePicture(newPictureUrl)
        setPicturePreview(newPictureUrl)
      }
      setProfileRecord({
        ...profileRecord,
        contact_person: [profileFields.first_name.trim(), profileFields.last_name.trim()].filter(Boolean).join(' '),
        phone: profileFields.phone.trim(),
        position: profileFields.position.trim(),
      })
      if (newLogoUrl) setLogoPreview(newLogoUrl)
      setCompany({
        ...company,
        description: companyFields.description.trim(),
        location: companyFields.location.trim(),
        website: companyFields.website.trim() || undefined,
        logo_url: newLogoUrl || undefined,
      })
      await refreshUser()
    } catch (err: any) {
      toast.error(err.message || 'Failed to save profile.')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
    setPictureFile(null)
    setPicturePreview(profilePicture || '')
    setLogoFile(null)
    setLogoPreview(company?.logo_url || '')
    const nameParts = (profileRecord?.contact_person || '').split(' ')
    setProfileFields({
      first_name: nameParts[0] || '',
      last_name: nameParts.slice(1).join(' '),
      phone: profileRecord?.phone || '',
      position: profileRecord?.position || '',
    })
    setCompanyFields({
      description: company?.description || '',
      location: company?.location || '',
      website: company?.website || '',
    })
  }

  const openPartnershipRequestModal = async () => {
    if (!company?.id) return

    setShowPartnershipRequestModal(true)
    setLoadingDepartments(true)
    setSelectedDepartmentId('')

    try {
      const { data, error } = await supabaseClient
        .from('departments')
        .select('id, name, abbreviation')
        .order('name', { ascending: true })

      if (error) throw error

      const now = Date.now()
      const cooldownMs = PARTNERSHIP_REAPPLY_COOLDOWN_DAYS * 24 * 60 * 60 * 1000

      const partnershipByDepartmentId = new Map(partnerships.map(p => [p.department_id, p]))
      const filtered = (data || []).filter((dept: DepartmentOption) => {
        const existing = partnershipByDepartmentId.get(dept.id)
        if (!existing) return true

        if (existing.status !== 'rejected') return false

        const rejectedAtRaw = existing.approved_at || existing.updated_at
        if (!rejectedAtRaw) return false

        const rejectedAtMs = new Date(rejectedAtRaw).getTime()
        if (Number.isNaN(rejectedAtMs)) return false

        return now - rejectedAtMs >= cooldownMs
      })
      setAvailableDepartments(filtered)
    } catch (err) {
      console.error('Error loading departments for partnership request:', err)
      toast.error('Unable to load departments right now.')
      setShowPartnershipRequestModal(false)
    } finally {
      setLoadingDepartments(false)
    }
  }

  const handleSubmitPartnershipRequest = async () => {
    if (!selectedDepartmentId) {
      toast.error('Please select a department first.')
      return
    }

    setRequestingPartnership(true)
    try {
      const { data, error } = await supabaseClient.rpc('request_employer_partnership_with_cooldown', {
        p_department_id: selectedDepartmentId,
        p_cooldown_days: PARTNERSHIP_REAPPLY_COOLDOWN_DAYS,
      })

      if (error) {
        if (error.message?.toLowerCase().includes('cooldown active until')) {
          toast.error(error.message)
        } else if (error.code === '23505') {
          toast.error('This department already has an existing partnership record for your company.')
        } else {
          throw error
        }
        return
      }

      const action = (data as any)?.action
      toast.success(
        action === 'resubmitted'
          ? 'Partnership request resubmitted. Admins will review it again.'
          : 'Partnership request submitted. We will notify admins for review.'
      )
      setShowPartnershipRequestModal(false)
      setSelectedDepartmentId('')
      await fetchProfile()
    } catch (err: any) {
      toast.error(err.message || 'Failed to submit partnership request.')
    } finally {
      setRequestingPartnership(false)
    }
  }

  const getReapplyAvailability = (partnership: PartnershipItem) => {
    const rejectedAtRaw = partnership.approved_at || partnership.updated_at
    if (!rejectedAtRaw) return null

    const rejectedAt = new Date(rejectedAtRaw)
    if (Number.isNaN(rejectedAt.getTime())) return null

    const availableAt = new Date(rejectedAt.getTime() + (PARTNERSHIP_REAPPLY_COOLDOWN_DAYS * 24 * 60 * 60 * 1000))
    return {
      isAvailableNow: Date.now() >= availableAt.getTime(),
      label: availableAt.toLocaleString('en-PH', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }),
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    )
  }

  const hasApprovedIndustryPartner = isPartner && profileRecord?.approval_status === 'approved'

  return (
    <div className="space-y-6 max-w-5xl mx-auto p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Employer Profile</h1>
          <p className="mt-2 text-gray-600">Manage your company and contact details</p>
        </div>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Pencil className="w-4 h-4" />
            Edit Profile
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={handleCancel}
              className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <Camera className="w-6 h-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">Profile Picture</h2>
        </div>
        <div className="flex items-center gap-6">
          <div className="relative">
            <div className="w-28 h-28 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center border border-gray-300">
              {picturePreview ? (
                <img src={picturePreview} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <User className="w-14 h-14 text-gray-400" />
              )}
            </div>
            {isEditing && (
              <label
                htmlFor="employer-profile-picture-upload"
                className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full cursor-pointer hover:bg-blue-700 transition-colors"
              >
                <Camera className="w-4 h-4" />
                <input
                  id="employer-profile-picture-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={e => {
                    const file = e.target.files?.[0]
                    if (file) {
                      setPictureFile(file)
                      setPicturePreview(URL.createObjectURL(file))
                    }
                  }}
                />
              </label>
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-semibold text-gray-900">{[profileFields.first_name, profileFields.last_name].filter(Boolean).join(' ') || company?.name || 'Employer'}</h3>
              {hasApprovedIndustryPartner && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                  <Building2 className="w-3.5 h-3.5" />
                  UCLM Industry Partner
                </span>
              )}
              {profileRecord?.approval_status && (
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                  profileRecord.approval_status === 'approved'
                    ? 'bg-green-100 text-green-700'
                    : profileRecord.approval_status === 'rejected'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {profileRecord.approval_status.charAt(0).toUpperCase() + profileRecord.approval_status.slice(1)}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">{company?.name || 'Company not set'}</p>
            <p className="text-sm text-gray-500">{user?.email || 'No email found'}</p>
            {user?.created_at && (
              <p className="text-xs text-gray-500 mt-2">
                Joined on {new Date(user.created_at).toLocaleDateString('en-PH', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            )}
            {isEditing && (
              <p className="text-xs text-gray-500 mt-2">Click the camera icon to upload a new profile picture.</p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <Building2 className="w-6 h-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">Company Information</h2>
        </div>

        {/* Company Logo */}
        <div className="flex items-center gap-5 mb-6 pb-6 border-b border-gray-100">
          <div className="relative flex-shrink-0">
            <div className="w-24 h-24 rounded-xl overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center">
              {logoPreview ? (
                <img src={logoPreview} alt="Company logo" className="w-full h-full object-contain p-1" />
              ) : (
                <Building2 className="w-10 h-10 text-gray-300" />
              )}
            </div>
            {isEditing && (
              <label
                htmlFor="company-logo-upload"
                className="absolute bottom-0 right-0 bg-blue-600 text-white p-1.5 rounded-full cursor-pointer hover:bg-blue-700 transition-colors"
              >
                <Camera className="w-3.5 h-3.5" />
                <input
                  id="company-logo-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={e => {
                    const file = e.target.files?.[0]
                    if (file) {
                      setLogoFile(file)
                      setLogoPreview(URL.createObjectURL(file))
                    }
                  }}
                />
              </label>
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">Company Logo</p>
            <p className="text-xs text-gray-500 mt-0.5">Optional. Shown to admins and on your company profile.</p>
            {isEditing && logoPreview && (
              <button
                type="button"
                onClick={() => { setLogoFile(null); setLogoPreview('') }}
                className="mt-2 text-xs text-red-500 hover:text-red-700 transition-colors"
              >
                Remove logo
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
            <input
              type="text"
              value={company?.name || ''}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-50 disabled:text-gray-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Industry</label>
            <input
              type="text"
              value={company?.industry || ''}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-50 disabled:text-gray-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Company Size</label>
            <input
              type="text"
              value={company?.company_size_text || ''}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-50 disabled:text-gray-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
            <input
              type="url"
              value={companyFields.website}
              onChange={e => setCompanyFields(prev => ({ ...prev, website: e.target.value }))}
              disabled={!isEditing}
              placeholder="https://www.company.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
            <div className="relative">
              <MapPin className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={companyFields.location}
                onChange={e => setCompanyFields(prev => ({ ...prev, location: e.target.value }))}
                disabled={!isEditing}
                placeholder="e.g. Mandaue City, Cebu"
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
              />
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Company Description</label>
            <textarea
              value={companyFields.description}
              onChange={e => setCompanyFields(prev => ({ ...prev, description: e.target.value }))}
              rows={4}
              disabled={!isEditing}
              placeholder="Describe your company, mission, and culture..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600 resize-none"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <Users className="w-6 h-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">Contact Information</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
            <input
              type="text"
              value={profileFields.first_name}
              onChange={e => setProfileFields(prev => ({ ...prev, first_name: e.target.value }))}
              disabled={!isEditing}
              placeholder="First name"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
            <input
              type="text"
              value={profileFields.last_name}
              onChange={e => setProfileFields(prev => ({ ...prev, last_name: e.target.value }))}
              disabled={!isEditing}
              placeholder="Last name"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Position / Title</label>
            <input
              type="text"
              value={profileFields.position}
              onChange={e => setProfileFields(prev => ({ ...prev, position: e.target.value }))}
              disabled={!isEditing}
              placeholder="e.g. HR Manager"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
            <div className={`flex rounded-lg border overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 border-gray-300 ${!isEditing ? 'bg-gray-50' : 'bg-white'}`}>
              <PhoneInput
                international
                defaultCountry="PH"
                value={profileFields.phone}
                onChange={(val) => setProfileFields(prev => ({ ...prev, phone: val || '' }))}
                disabled={!isEditing}
                className="phone-input-custom w-full"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
            <div className="relative">
              <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-50 disabled:text-gray-600"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Website (Quick Link)</label>
            {companyFields.website ? (
              <a
                href={companyFields.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline"
              >
                <Globe className="w-4 h-4" />
                {companyFields.website}
              </a>
            ) : (
              <p className="text-sm text-gray-500">No website set</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Account Created</label>
            <div className="inline-flex items-center gap-2 text-sm text-gray-700">
              <CalendarDays className="w-4 h-4 text-gray-500" />
              {user?.created_at
                ? new Date(user.created_at).toLocaleDateString('en-PH', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : 'Not available'}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <FileText className="w-6 h-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">Signup Snapshot</h2>
        </div>
        <p className="text-sm text-gray-600">
          These are your core signup details: company name, industry, contact person, phone, email, company size, and website.
        </p>
      </div>

      {company && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <Building2 className="w-6 h-6 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900">Industry Partnerships</h2>
          </div>
          {!hasApprovedIndustryPartner && (
            <p className="text-sm text-gray-600 mb-4">
              Apply to become an industry partner by sending a department partnership request.
            </p>
          )}
          {partnerships.length > 0 ? (
            <div className="space-y-2">
              {partnerships.map((p, i) => (
                <div key={i} className="px-4 py-2 rounded-lg bg-gray-50 border border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-800">{p.abbreviation ? `${p.name} (${p.abbreviation})` : p.name}</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                      p.status === 'approved'
                        ? 'bg-green-100 text-green-700'
                        : p.status === 'rejected'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                    </span>
                  </div>
                  {p.status === 'rejected' && p.rejection_reason && (
                    <p className="mt-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded-md px-2 py-1">
                      Reason: {p.rejection_reason}
                    </p>
                  )}
                  {p.status === 'rejected' && (() => {
                    const reapply = getReapplyAvailability(p)
                    if (!reapply) return null

                    return (
                      <p className={`mt-2 text-xs rounded-md px-2 py-1 border ${
                        reapply.isAvailableNow
                          ? 'text-green-700 bg-green-50 border-green-100'
                          : 'text-amber-700 bg-amber-50 border-amber-100'
                      }`}>
                        {reapply.isAvailableNow
                          ? 'Re-apply is available now.'
                          : `Re-apply available on ${reapply.label}`}
                      </p>
                    )
                  })()}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No department partnership requests yet.</p>
          )}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={openPartnershipRequestModal}
              className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
            >
              {hasApprovedIndustryPartner ? 'Request partnership with another department' : 'Apply for industry partnership'}
            </button>
          </div>
        </div>
      )}

      {createPortal(<>
      {showPartnershipRequestModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md bg-white rounded-xl shadow-xl border border-gray-200 p-5">
            <h3 className="text-lg font-semibold text-gray-900">Request New Department Partnership</h3>
            <p className="mt-1 text-sm text-gray-600">
              Send a low-priority request to partner with another department.
            </p>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
              <select
                value={selectedDepartmentId}
                onChange={e => setSelectedDepartmentId(e.target.value)}
                disabled={loadingDepartments || requestingPartnership || availableDepartments.length === 0}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              >
                <option value="">Select a department</option>
                {availableDepartments.map(dept => (
                  <option key={dept.id} value={dept.id}>
                    {dept.abbreviation ? `${dept.name} (${dept.abbreviation})` : dept.name}
                  </option>
                ))}
              </select>
              {!loadingDepartments && availableDepartments.length === 0 && (
                <p className="mt-2 text-xs text-gray-500">All departments already have an existing request or partnership.</p>
              )}
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowPartnershipRequestModal(false)}
                disabled={requestingPartnership}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitPartnershipRequest}
                disabled={requestingPartnership || loadingDepartments || !selectedDepartmentId}
                className="px-3 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {requestingPartnership ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
      </>, document.body)}
    </div>
  )
}

export default EmployerProfilePage
